import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@chakra-ui/progress';
import { AlarmIndicator, DotsIcons } from './UiElements';
import { LocalIconProvider } from './LocalIconProvider';
import React from 'react';
import usePersistStore from '../stores/persistStore';
import { useItemSize } from '../Utils/useItemSize';

interface FavIconProps {
    size?: string;
    url?: string;
    favIcon?: string;
    loading?: boolean;
    hoverSite?: boolean;
    isActiveTab?: boolean;
    title?: string;
    noShadow?: boolean;
    mr?: string;
    my?: string;
    border?: boolean;
    innerFav?: boolean;
    isInStack?: boolean;
    hasAlarm?: any;
    isDiscarded?: boolean;
    isPinned?: boolean;
}

const FavIcon: React.FC<FavIconProps> = React.memo(
    ({
        size = 'default',
        url,
        favIcon,
        loading,
        hoverSite,
        isActiveTab,
        title,
        noShadow = false,
        mr = '16px',
        my = '4px',
        border = false,
        innerFav = false,
        isInStack = false,
        hasAlarm = null,
        isDiscarded = false,
        isPinned = false
    }) => {
        const bgDark = isInStack ? '#3c4042' : 'transparent';
        const bg = useColorModeValue('#FFF', bgDark);
        const bgActive = useColorModeValue('#FFF', '#3c4042'); //'#3c4042'
        const outerShadow = useColorModeValue(
            '0 1px 4px -1px rgba(78,80,81,0.4), 0 1px 2px 0px rgba(78,80,81,0.22)', //Inner shadow: inset 0 -2px 3px -1px rgba(78,80,81,0.12)
            '' //'0 1px 4px -1px rgba(0,0,0,0.80)'
        );
        const isInStackShadow = useColorModeValue(
            '0 1px 4px -1px rgba(78,80,81,0.4), 0 1px 2px 0px rgba(78,80,81,0.22)',
            '0 1px 4px -1px rgba(0,0,0,0.80)'
        );

        const { favIconOuterSize, favIconSize, todoRounded } = useItemSize();

        const outerSize = isActiveTab
            ? size === '24px'
                ? '24px'
                : '32px'
            : size === 'default'
            ? favIconOuterSize
            : size;

        if (innerFav)
            return (
                <InnerImg
                    favIcon={favIcon}
                    loading={loading}
                    size={size}
                    favIconSize={favIconSize}
                    title={title}
                    url={url}
                />
            );

        return (
            <Flex
                transition="0.15s"
                alignItems="center"
                rounded={todoRounded}
                minWidth={outerSize}
                minHeight={outerSize}
                maxHeight={outerSize}
                maxWidth={outerSize}
                bg={isActiveTab ? bgActive : bg}
                boxShadow={
                    noShadow ? '' : isInStack ? isInStackShadow : outerShadow
                }
                mr={mr}
                my={my}
                pos="relative"
                opacity={isDiscarded ? '0.5' : null}
                filter={isDiscarded ? 'grayscale(1)' : null}
            >
                <InnerImg
                    favIcon={favIcon}
                    loading={loading}
                    size={size}
                    favIconSize={favIconSize}
                    title={title}
                    url={url}
                />
                {loading ? (
                    <Flex position="absolute" h="100%" w="100%">
                        <CircularProgress
                            transform={loading ? 'scale(1)' : 'scale(3)'}
                            opacity={loading ? '1' : '0'}
                            transition="0.4s 0.5 linear"
                            m="auto"
                            size={favIconSize}
                            isIndeterminate
                            color="blue.400"
                        ></CircularProgress>
                    </Flex>
                ) : (
                    ''
                )}
                {hasAlarm && (
                    <AlarmIndicator
                        hasAlarm={hasAlarm}
                        type={'site'}
                        hoverSite={hoverSite}
                    />
                )}
                {isPinned && (
                    <div style={{ position: 'absolute', top: -7, right: -4 }}>
                        <PinnedIcon />
                    </div>
                )}
            </Flex>
        );
    }
);

export default FavIcon;

const InnerImg = ({ favIcon, loading, size, favIconSize, title, url }) => {
    const { colorMode } = useColorMode();
    const [fallbackActive, setFallbackActive] = useState(false);
    const customSize = {
        '24px': '16px',
        '32px': '20px',
        '44px': '24px'
    };

    const gitHubFavIcon =
        colorMode === 'light'
            ? 'https://github.githubassets.com/favicons/favicon.svg'
            : 'https://github.githubassets.com/favicons/favicon-dark.svg';

    const localIcon =
        favIcon === 'localIcon' ? LocalIconProvider(title) : false;

    const fallbackSrc =
        colorMode === 'light'
            ? '../../assets/images/fallbackSvg.svg'
            : '../../assets/images/fallbackSvgDark.svg';

    const isGitHub = url?.startsWith('https://github');

    const favSize = size === 'default' ? favIconSize : customSize?.[size];

    const favIconSrc = useMemo(() => {
        if (fallbackActive) {
            return fallbackSrc;
        } else if (favIcon) {
            if (isGitHub) {
                return gitHubFavIcon;
            }
            return localIcon || favIcon;
        } else {
            try {
                let domain = new URL(url).hostname;
                let googleApiUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;

                return googleApiUrl;
            } catch (e) {
                return fallbackSrc;
            }
        }
        // console.log('return fallbackSrc', favIcon);
        // return fallbackSrc;
    }, [favIcon, isGitHub, localIcon, fallbackSrc, fallbackActive]);

    useEffect(() => {
        // Reset fallback state whenever favIcon changes
        setFallbackActive(false);
    }, [favIcon]);
    const onLoadSuccess = (e) => {
        if (e.target.naturalHeight + e.target.naturalWidth === 0) {
            setFallbackActive(true);
        }
    };

    return (
        <img
            src={fallbackActive ? fallbackSrc : favIconSrc}
            alt="Fav icon"
            onError={() => setFallbackActive(true)}
            onLoad={(e) => onLoadSuccess(e)}
            style={{
                fontSize: '0px',
                margin: 'auto',
                borderRadius: '2px',
                transform: loading ? 'scale(0.5)' : '',
                transition: loading ? '0.2s linear' : '',
                textAlign: 'center',
                fontWeight: '500',
                width: favSize,
                height: favSize,
                maxHeight: favSize,
                maxWidth: favSize,
                userSelect: 'none'
                /* maxWidth: '24px',
            maxHeight: '24px' */
            }}
        />
    );
};

const PinnedIcon = () => {
    const color = useColorModeValue('#BBC0CF', '#9ba6b0');

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            style={{ width: '14px', height: '14px' }}
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10C21 13.1191 19.2561 15.3655 17.4713 17.2244C17.0407 17.673 16.5965 18.1098 16.1671 18.5321L16.1114 18.5869C15.6608 19.0301 15.2274 19.4575 14.8192 19.8878C13.9985 20.7526 13.3284 21.5792 12.8944 22.4472C12.725 22.786 12.3788 23 12 23C11.6212 23 11.275 22.786 11.1056 22.4472C10.6716 21.5792 10.0015 20.7526 9.18085 19.8878C8.77261 19.4575 8.33924 19.0301 7.88863 18.5869L7.8328 18.532C7.40342 18.1097 6.9593 17.673 6.52865 17.2244C4.74387 15.3655 3 13.1191 3 10ZM15 9.5C15 11.1569 13.6569 12.5 12 12.5C10.3431 12.5 9 11.1569 9 9.5C9 7.84315 10.3431 6.5 12 6.5C13.6569 6.5 15 7.84315 15 9.5Z"
                fill={color}
            ></path>
        </svg>
    );
};
